import { useState, type ReactNode } from "react";
import { Icon } from "@/components/ui/Icon";
import type { UserCalendarsApi } from "@/hooks/useUserCalendars";
import type { GoogleCalendarApi } from "@/hooks/useGoogleCalendar";
import { VIS_TODO, calendarColorCss, visKeyGoogle, visKeyProd } from "@/domain/calendar";
import { CalendarSettingsModal } from "./CalendarSettingsModal";

/**
 * 캘린더 목록 카드 — 내 캘린더 / 연동 캘린더 / 법인 생산 을 켜고 끄고, ⚙ 로 설정 모달(추가·삭제·Google 가져오기) (2026-10-29).
 * 옛 GoogleCalendarCard(연결·동기화·해제 + 표시 체크)를 흡수했다. 체크 = 달력 표시(서버 저장, 기기 간 공유). 체크박스 자체가 캘린더 색(테두리·채움, 색 칩 없음).
 *
 * 상태별: 로딩 = 카드 숨김 / Google 미연결 = 연동 절에 [연결] 한 줄(누르면 설정 모달) / 가져온 캘린더 0 = 안내 한 줄
 * 묶음 접기(2026-10-29): 묶음 머리에 마우스를 올리면 접기 아이콘, 누르면 접힘(접힌 동안은 아이콘 상시 + 건수). 이 브라우저에만 기억(localStorage).
 */

const COLLAPSE_KEY = "rmc.calList.collapsed";
function readCollapsed(): Set<string> {
  try {
    const arr: unknown = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "[]");
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : []);
  } catch { return new Set(); }
}

function Group({
  id, title, sub, count, collapsed, onToggle, children,
}: { id: string; title: string; sub?: ReactNode; count: number; collapsed: boolean; onToggle: (id: string) => void; children: ReactNode }) {
  return (
    <div className={`my-cl-group${collapsed ? " is-collapsed" : ""}`}>
      <button type="button" className="my-cl-ghd" onClick={() => onToggle(id)} aria-expanded={!collapsed} aria-controls={`my-cl-${id}`}>
        <span className="my-cl-ghd-title">{title}</span>
        {sub && <span className="my-cl-ghd-sub">{sub}</span>}
        {collapsed && <span className="my-cl-ghd-count">{count}</span>}
        <Icon name="chevD" size={12} />
      </button>
      {!collapsed && <div id={`my-cl-${id}`}>{children}</div>}
    </div>
  );
}
interface Props {
  cal: UserCalendarsApi;
  g: GoogleCalendarApi;
  showJoongang: boolean;
  showGyeongwon: boolean;
  gyeongwonMock?: boolean;
}

export function CalendarListCard({ cal, g, showJoongang, showGyeongwon, gyeongwonMock }: Props) {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(readCollapsed);
  const toggle = (id: string) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id); else next.add(id);
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next])); } catch { /* 이번 창에서만 */ }
  };
  if (cal.mode === "loading" || g.mode === "loading") return null;

  // 색 칩 대신 체크박스 자체가 캘린더 색(테두리 = 색, 켜면 채움) — 꺼도 어느 캘린더인지 색으로 보인다
  const row = (key: string, color: string, name: string, extra?: React.ReactNode) => (
    <li key={key} className="my-row my-cl-row">
      <label className="my-gcal-item">
        <input
          type="checkbox"
          className="my-cl-check"
          checked={cal.isVisible(key)}
          onChange={(e) => void cal.setVisible(key, e.target.checked)}
          style={{ "--chk-c": color } as React.CSSProperties}
        />
        <span className="my-row-title">{name}</span>
      </label>
      {extra}
    </li>
  );

  const googleLinked = g.mode === "linked";
  return (
    <>
      <section className="card my-cl">
        <div className="card-hd">
          <div>
            <div className="card-title">캘린더</div>
            <div className="card-sub">체크한 캘린더만 달력에 보입니다</div>
          </div>
          <div className="card-actions">
            <button type="button" className="btn-tiny my-cl-gear" onClick={() => setOpen(true)} aria-label="캘린더 설정">
              <Icon name="settings" size={14} />
              설정
            </button>
          </div>
        </div>

        <Group id="mine" title="내 캘린더" count={cal.calendars.length + 1} collapsed={collapsed.has("mine")} onToggle={toggle}>
          <ul className="my-list">
            {cal.calendars.map((c) => row(c.id, calendarColorCss(c.color), c.name, c.builtin ? <span className="my-tag">기본</span> : undefined))}
            {row(VIS_TODO, "var(--c-slate-400)", "할 일", undefined)}
          </ul>
        </Group>

        <Group
          id="linked"
          title="연동 캘린더"
          sub={googleLinked ? <>Google · {g.state?.email}</> : undefined}
          count={googleLinked ? g.imported.length : 0}
          collapsed={collapsed.has("linked")}
          onToggle={toggle}
        >
          {googleLinked ? (
            <ul className="my-list">
              {g.imported.map((c) =>
                row(
                  visKeyGoogle(c.id),
                  c.color || "var(--c-info)",
                  c.name,
                  <span className="my-tbadge sp-google">Google</span>,
                ),
              )}
              {g.imported.length === 0 && (
                <li className="my-row my-cl-row">
                  <span className="my-row-title my-muted">가져온 캘린더가 없습니다</span>
                  <button type="button" className="btn-tiny" onClick={() => setOpen(true)}>가져오기</button>
                </li>
              )}
            </ul>
          ) : (
            <ul className="my-list">
              <li className="my-row my-cl-row">
                <span className="my-tbadge sp-google">Google</span>
                <span className="my-row-title my-muted">
                  {g.mode === "needs_relink" ? "다시 연결이 필요합니다" : g.mode === "unavailable" ? "준비 중" : "연결 안 됨"}
                </span>
                {(g.mode === "unlinked" || g.mode === "needs_relink") && (
                  <button type="button" className="btn-tiny" onClick={() => setOpen(true)}>
                    {g.mode === "needs_relink" ? "다시 연결" : "연결"}
                  </button>
                )}
              </li>
            </ul>
          )}
        </Group>

        {(showJoongang || showGyeongwon) && (
          <Group id="prod" title="법인 생산" count={(showJoongang ? 1 : 0) + (showGyeongwon ? 1 : 0)} collapsed={collapsed.has("prod")} onToggle={toggle}>
            <ul className="my-list">
              {showJoongang && row(visKeyProd("joongang"), "var(--c-tenant-joongang)", "중앙 생산 (㎥)")}
              {showGyeongwon && row(visKeyProd("gyeongwon"), "var(--c-tenant-gyeongwon)", `경원 생산 (톤)${gyeongwonMock ? " · 가상" : ""}`)}
            </ul>
          </Group>
        )}
      </section>

      {open && <CalendarSettingsModal cal={cal} g={g} onClose={() => setOpen(false)} />}
    </>
  );
}
